import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  ViewChild,
  inject,
  input,
  signal,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { AssetDto, DisplayUserEmail, GalleryDto, GalleryFeedbackOptions } from '@nusign/picflow-backend-types';
import { DynamicHooksComponent } from 'ngx-dynamic-hooks';
import { MentionComponent } from '../../../gallery/components/mention/mention.component';
import { fixContentEditableStyles } from '../../../lib/fix-contenteditable-styles';
import { getMentionHtml } from '../../../lib/get-mention-html';
import { isMetaKey } from '../../../lib/is-meta-key';
import { replaceMentions } from '../../../lib/replace-mentions';
import { AvatarService } from '../../../services/avatar.service';
import { CommentService } from '../../../services/comment.service';
import { RelativeTimePipe } from '../../../shared-extra/pipes/relative-time.pipe';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { ButtonMenuItemComponent } from '../../../shared/components/button-menu-item/button-menu-item.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { WysiwygDirective } from '../../../wysiwyg/directives/wysiwyg.directive';
import { IsUndefinedOrNullPipe } from '../../pipes/is-undefined-or-null.pipe';
import { PdfPageNumberPipe } from '../../pipes/pdf-page-number.pipe';
import { VideoTimePercentToFrameCountPipe } from '../../pipes/video-time-percent-to-frame-count.pipe';
import { VideoTimePercentToStringPipe } from '../../pipes/video-time-percent-to-string.pipe';
import { Comment, CommentThread } from '../comments/comments.component';

@Component({
  selector: 'app-comment-item',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './comment-item.component.html',
  styleUrl: './comment-item.component.scss',
  imports: [
    AvatarComponent,
    ContextMenuComponent,
    ButtonDirective,
    SvgComponent,
    RelativeTimePipe,
    ButtonMenuItemComponent,
    WysiwygDirective,
    TooltipDirective,
    VideoTimePercentToStringPipe,
    VideoTimePercentToFrameCountPipe,
    DatePipe,
    IsUndefinedOrNullPipe,
    DynamicHooksComponent,
    PdfPageNumberPipe,
  ],
})
export class CommentItemComponent implements OnChanges {
  private check = inject(ChangeDetectorRef);
  private commentService = inject(CommentService);
  public sanitization = inject(DomSanitizer);
  public avatarService = inject(AvatarService);
  @HostListener('window:keydown', ['$event'])
  keyEvent(event: KeyboardEvent) {
    const metaKey = isMetaKey(event);
    if (event.target['getAttribute']?.('name') === 'edit-comment') {
      if (event.key === 'Enter' && metaKey) this.saveEditing(event);
    }
  }
  @ViewChild('editComment') editComment: ElementRef<HTMLDivElement>;

  @Input() comment: Comment | CommentThread;
  @Input({ required: true }) galleryPermissions: GalleryDto['permissions'];
  @Input() enabledAndAllowedFeedbackOptions: GalleryFeedbackOptions[];
  @Input() myId: string;
  @Input() expandReplies: boolean;
  @Input() asset: AssetDto;
  replies = input<Comment[]>();

  @Output() deleteComment = new EventEmitter<Comment | CommentThread>();
  @Output() resolveThread = new EventEmitter<boolean>();
  @Output() updateComment = new EventEmitter<string>();
  @Output() unselect = new EventEmitter<void>();

  thread = signal<CommentThread>(null);
  editing = signal(false);
  editingText = '';

  parsers = [MentionComponent];

  ngOnChanges(): void {
    this.thread.set('replies' in this.comment ? this.comment : null);
    this.check.detectChanges();
  }

  toggleResolved(event: Event) {
    event.stopPropagation();
    this.resolveThread.emit(!this.comment.resolved);
  }

  toggleEditing(event: Event) {
    if (!this.comment) return;
    this.editing.set(!this.editing());
    this.editingText = this.comment.text || '';

    this.editingText = this.editingText.replace(
      /<app-mention data-user-id="([^"]+)" \[user\]="[^"]+">.*?<\/app-mention>/gs,
      (match, userId) => {
        const user = (this.comment.mentions as { user: DisplayUserEmail }[])?.find(
          mention => mention.user.id === userId
        ).user;
        if (!user) return '';
        return getMentionHtml(user, this.avatarService.getAvatar(user));
      }
    );

    event.stopPropagation();
    if (this.editing) setTimeout(() => this.editComment?.nativeElement?.focus());
  }

  cancelEditing(event: Event) {
    this.editing.set(false);
    event.stopPropagation();
  }

  saveEditing(event: Event) {
    const text = replaceMentions(fixContentEditableStyles(this.editingText).trim());

    this.updateComment.emit(text);
    this.comment.text = text;
    this.commentService.makeCommentPresentable(this.comment);
    this.editing.set(false);
    event.stopPropagation();
  }

  toggleReplies(event: Event) {
    if (this.expandReplies) {
      this.unselect.emit();
      event.stopPropagation();
    }
  }

  stopPropagation(event: Event) {
    event.stopPropagation();
  }
}
